import {
  ArrowRight,
  Award,
  CheckCircle,
  Shield,
  Star,
  XCircle,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Competitors data
const competitors = [
  {
    id: 'servicenow',
    name: 'ServiceNow',
    category: 'GRC & ITSM',
    description: 'See how BOSSS compares to ServiceNow for GRC, security operations, and IT service management.',
    logo: null,
    color: 'from-green-500 to-green-600',
  },
  {
    id: 'splunk',
    name: 'Splunk',
    category: 'SIEM & Security Analytics',
    description: 'Compare BOSSS XDR capabilities with Splunk for security monitoring and threat detection.',
    logo: null,
    color: 'from-orange-500 to-orange-600',
  },
  {
    id: 'crowdstrike',
    name: 'CrowdStrike',
    category: 'Endpoint Security & XDR',
    description: 'Discover how BOSSS provides unified XDR alongside GRC and IAM, unlike point solutions.',
    logo: null,
    color: 'from-red-500 to-red-600',
  },
  {
    id: 'sailpoint',
    name: 'SailPoint',
    category: 'Identity Governance',
    description: 'Compare BOSSS IAM with SailPoint for identity governance and access management.',
    logo: null,
    color: 'from-blue-500 to-blue-600',
  },
  {
    id: 'archer',
    name: 'Archer (RSA)',
    category: 'GRC Platform',
    description: 'See how BOSSS modernizes GRC compared to legacy Archer deployments.',
    logo: null,
    color: 'from-purple-500 to-purple-600',
  },
  {
    id: 'qualys',
    name: 'Qualys',
    category: 'Vulnerability Management',
    description: 'Compare BOSSS integrated security approach with Qualys vulnerability scanning.',
    logo: null,
    color: 'from-cyan-500 to-cyan-600',
  },
];

const comparisonHighlights = [
  {
    title: 'Unified Platform',
    bosss: 'Single platform for GRC, IAM, CMDB, and XDR',
    competitors: 'Multiple point solutions requiring integration',
    icon: Shield,
  },
  {
    title: 'Deployment Time',
    bosss: 'Weeks, not months',
    competitors: '6-18 months typical deployment',
    icon: Zap,
  },
  {
    title: 'Total Cost of Ownership',
    bosss: 'Predictable, all-inclusive pricing',
    competitors: 'High licensing + integration + maintenance costs',
    icon: Award,
  },
  {
    title: 'AI-Powered Automation',
    bosss: '10 AI voice agents built-in',
    competitors: 'Limited or add-on AI capabilities',
    icon: Star,
  },
];

export function CompareHub() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <div className="inline-flex items-center bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-500/30">
              <Shield className="w-4 h-4 mr-2" />
              Why BOSSS
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Compare BOSSS vs. Competitors
            </h1>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              See how BOSSS unified cybersecurity platform stacks up against point solutions and legacy vendors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Comparison Highlights */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              The BOSSS Difference
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {comparisonHighlights.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={item.title}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full border-gray-200">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>

                      <h3 className="font-semibold text-gray-900 mb-3">{item.title}</h3>

                      <div className="space-y-2">
                        <div className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />

                          <span className="text-sm text-gray-700">
                            <strong>BOSSS:</strong>
                            {' '}
                            {item.bosss}
                          </span>
                        </div>

                        <div className="flex items-start">
                          <XCircle className="w-4 h-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />

                          <span className="text-sm text-gray-500">
                            <strong>Others:</strong>
                            {' '}
                            {item.competitors}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Competitor Comparison Cards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Detailed Comparisons
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose a competitor to see a detailed feature-by-feature comparison.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competitors.map((competitor, index) => (
              <motion.div
                key={competitor.id}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="h-full border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => onNavigateToTop(`/compare/${competitor.id}`)}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${competitor.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-white font-bold text-lg">{competitor.name.charAt(0)}</span>
                    </div>

                    <div className="mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{competitor.category}</span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      BOSSS vs.
                      {' '}
                      {competitor.name}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4">
                      {competitor.description}
                    </p>

                    <div className="flex items-center text-blue-600 font-medium text-sm">
                      <span>View comparison</span>
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Switch to BOSSS?
            </h2>

            <p className="text-xl text-blue-100 mb-8">
              Consolidate your security stack with one unified platform that does it all.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-semibold"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule a Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="px-8 py-4 border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 font-semibold backdrop-blur-sm"
                onClick={() => onNavigateToTop('/contact')}
              >
                Talk to Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default CompareHub;
